import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import {
  TableBody,
  TableContainer,
  TableSortLabel,
  TableHead,
  Typography,
  TableRow,
  TableCell,
  Collapse,
  Checkbox,
  Stack,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { difference, union } from 'lodash';

import { Model } from '../types';
import { Pagination } from '../components';
import { mapGroupBy } from '../utils/array';
import { DataTableColumnProps } from './DataTableColumnProps';
import * as Sx from './DataTable.styles';
import { DataTableProps } from './DataTable.props';
import { DataTableRow, StyledTable, StyledTableRow, StyledTableCell } from '.';

type GroupItem = {
  groupKey: string;
  isGroup: boolean;
};

export const DataTable = <T extends Model>({
  compactHeader = false,
  children,
  data,
  isDisabledFunction,
  isSelectedFunction,
  loading,
  menuItems,
  pagination,
  sorting,
  rowCollapseContent,
  rowCollapseOpened,
  rowOnClick,
  selected,
  setSelectAll,
  mobileBorder,
  mobileHideMenuItems = false,
  noResultElement,
  tableBodyStyles,
  tableHeaderStyles,
  tableStyles,
  specialFirstRow,
  disableActiveItem = false,
  isRowOnError,
  groupKey,
  renderGroupHeader,
  openedGroup,
  setOpenedGroup,
  customRowKey = (item: T) => item.id,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<T | null>(null);

  const extractedChildren = useMemo(() => {
    const result = [];
    for (const child of children) {
      if ((child as ReactElement)?.type === Fragment) {
        const fragmentChildren = (child as ReactElement).props[
          'children'
        ] as Array<DataTableColumnProps<T>>;
        result.push(...fragmentChildren);
      } else {
        result.push(child);
      }
    }

    return result;
  }, [children]);

  const groupedData = useMemo(
    () => (groupKey && data ? mapGroupBy(data, groupKey) : null),
    [data, groupKey]
  );

  const flatGroupedData = useMemo(() => {
    if (!groupedData) {
      return null;
    }
    const result: (GroupItem | T)[] = [];
    groupedData.forEach((value, key) => {
      result.push(...[{ groupKey: key, isGroup: true }, ...value]);
    });
    return result;
  }, [groupedData]);

  const renderData = useMemo(
    () => flatGroupedData ?? data,
    [flatGroupedData, data]
  );

  const isGroupOpened = (key: string) => openedGroup?.includes(key);

  const toggleOpenGroup = (key: string) => {
    if (!setOpenedGroup || !openedGroup) {
      return;
    }
    if (isGroupOpened(key)) {
      setOpenedGroup(openedGroup.filter((item) => item !== key));
    } else {
      setOpenedGroup([...openedGroup, key]);
    }
  };

  const columns = extractedChildren.filter((x) => x) as Array<
    ReactElement<DataTableColumnProps<T>>
  >;

  const hasMenuItems = useMemo(
    () => menuItems && data?.some((item) => menuItems(item)),
    [data, menuItems]
  );

  const onRowClick = (dataItem: T) => {
    if (mobileBorder !== 'none') {
      if (activeItem === dataItem) {
        setActiveItem(null);
      } else {
        setActiveItem(dataItem);
      }
    }
    if (rowOnClick) {
      rowOnClick(dataItem);
    }
  };

  const selectAllChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectAll && data && setSelectAll((prev) => union(prev, data));
    } else {
      setSelectAll && data && setSelectAll((prev) => difference(prev, data));
    }
  };

  const getHeaderChildren = (child: ReactElement<DataTableColumnProps<T>>) => {
    let title: string | ReactNode = child.props.title;
    if (child.props.tooltip && child.props.title) {
      title = (
        <Tooltip title={child.props.tooltip}>
          <span>{child.props.title}</span>
        </Tooltip>
      );
    } else if (child.props.property.toString() === 'selectable') {
      title = (
        <Checkbox
          checked={
            selected &&
            data &&
            selected.length > 0 &&
            data.every((i) => selected.includes(i))
          }
          onChange={selectAllChecked}
        />
      );
    }
    return (
      <>
        {title}
        {sorting && !child.props.disableSort && data && data.length > 0 && (
          <TableSortLabel
            active={sorting?.sortField === child.props.property}
            direction={
              sorting &&
              sorting.sortField === child.props.property &&
              !Array.isArray(sorting.sortDirection)
                ? sorting.sortDirection
                : undefined
            }
            IconComponent={ArrowDropDownIcon}
            onClick={() =>
              sorting?.sortByField(child.props.property.toString())
            }
          />
        )}
      </>
    );
  };

  const renderGroupHeaderBase = (dataItem: GroupItem) => {
    const isOpen = isGroupOpened(dataItem.groupKey);
    return (
      <StyledTableRow
        key={dataItem.groupKey}
        onClick={() => toggleOpenGroup(dataItem.groupKey)}
        sx={Sx.groupHeaderRow}
      >
        {renderGroupHeader && groupedData ? (
          renderGroupHeader(groupedData.get(dataItem.groupKey) ?? [], !!isOpen)
        ) : (
          <StyledTableCell key="groupKey" colSpan={columns.length}>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{dataItem.groupKey}</Typography>
              <KeyboardArrowDownIcon
                key="expand"
                sx={Sx.groupHeaderArrowIcon(isOpen)}
              />
            </Stack>
          </StyledTableCell>
        )}
      </StyledTableRow>
    );
  };

  return (
    <TableContainer sx={tableStyles}>
      <StyledTable>
        <TableHead sx={tableHeaderStyles}>
          <StyledTableRow>
            {columns &&
              columns.map((child) => (
                <StyledTableCell
                  key={child.props.property.toString()}
                  onClick={() =>
                    sorting &&
                    !child.props.disableSort &&
                    sorting.sortByField(child.props.property.toString())
                  }
                  width={
                    child.props.property.toString() !== 'selectable'
                      ? child.props.width
                      : '42px'
                  }
                  sx={Sx.tableHeaderCell(
                    child.props,
                    !!sorting,
                    !!flatGroupedData?.length
                  )}
                  sortDirection={
                    sorting &&
                    child.props.disableSort &&
                    !Array.isArray(sorting.sortDirection)
                      ? sorting.sortDirection
                      : undefined
                  }
                >
                  {compactHeader ? (
                    <Stack
                      direction="row"
                      justifyContent="flex-start"
                      textAlign={child.props.textAlign ?? 'left'}
                    >
                      {getHeaderChildren(child)}
                    </Stack>
                  ) : (
                    getHeaderChildren(child)
                  )}
                </StyledTableCell>
              ))}
            {hasMenuItems && <StyledTableCell className="menu-items" />}
          </StyledTableRow>
          {specialFirstRow}
        </TableHead>

        <TableBody sx={tableBodyStyles}>
          {renderData &&
            renderData.map((dataItem, index) => {
              const isGroupItem =
                'groupKey' in dataItem && 'isGroup' in dataItem;
              const isOpen =
                groupKey &&
                isGroupOpened(
                  isGroupItem ? dataItem.groupKey : groupKey(dataItem)
                );
              return isGroupItem ? (
                renderGroupHeaderBase(dataItem)
              ) : (
                <React.Fragment key={customRowKey(dataItem)}>
                  <DataTableRow<T>
                    columns={columns}
                    dataItem={dataItem}
                    activeItem={activeItem}
                    rowIndex={index}
                    isOpen={isOpen}
                    mobileBorder={mobileBorder}
                    mobileHideMenuItems={mobileHideMenuItems}
                    selected={selected}
                    menuItems={menuItems}
                    hasMenuItems={hasMenuItems}
                    disableActiveItem={disableActiveItem}
                    onRowClick={() => {
                      onRowClick(dataItem);
                    }}
                    rowOnClick={rowOnClick}
                    isDisabledFunction={isDisabledFunction}
                    isSelectedFunction={isSelectedFunction}
                    isRowOnError={isRowOnError}
                    groupKey={groupKey}
                  />
                  {rowCollapseContent && rowCollapseOpened && (
                    <TableRow className="row-collapse-content">
                      <TableCell
                        style={{ padding: 0, borderBottom: 'none' }}
                        colSpan={
                          extractedChildren.length + (hasMenuItems ? 1 : 0)
                        }
                      >
                        <Collapse
                          sx={Sx.tableBodyCollapsibleCell(
                            isRowOnError?.(dataItem)
                          )}
                          in={rowCollapseOpened(dataItem)}
                        >
                          {rowCollapseOpened(dataItem) &&
                            rowCollapseContent(dataItem)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
        </TableBody>
      </StyledTable>
      {data &&
        data.length === 0 &&
        (loading === undefined || !loading) &&
        !specialFirstRow &&
        (noResultElement ? (
          noResultElement
        ) : (
          <Typography variant="h3" sx={Sx.noData}>
            {t('common.noItemsFound')}
          </Typography>
        ))}

      {pagination && (
        <Pagination
          pagination={pagination}
          disabled={!data || data.length === 0}
        />
      )}
    </TableContainer>
  );
};
